import { ArrowLeft, CalendarDays, MapPin, Search, UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPortalEvent, getPortalGuests, getPortalReport } from "../api/client";
import type { PortalEventDetail, PortalGuest } from "../api/types";
import {
  BrandMark,
  EmptyState,
  ErrorState,
  LoadingState,
  StatTile,
  StatusPill,
} from "../components/ui";
import { useBrandTheme } from "../lib/brandTheme";
import { formatDate, formatRange, formatSessionWindow, formatStamp, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";

// /events/:eventId — view-only event workspace with three tabs:
// Overview (description + logistics), Report (RSVP/check-in rollup +
// per-session table), Guests (full roster with client-side search).
// Report and Guests fetch lazily on first visit to their tab.

type Tab = "overview" | "report" | "guests";

export function EventDetail() {
  const { eventId = "" } = useParams();
  const [tab, setTab] = useState<Tab>("overview");
  const { data, error, loading, reload } = useApi(() => getPortalEvent(eventId), [eventId]);

  // Server merges the event's theme override over the brand kit's theme.
  useBrandTheme(data?.company?.theme ?? null);

  if (loading) return <LoadingState label="Loading event…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const { event, clientName, agencyName, company } = data;
  const accent = company?.accentColor ?? event.primaryColor ?? "#D5B56F";

  return (
    <div>
      <Link to="/" className="btn-quiet -ml-3 mb-5">
        <ArrowLeft size={14} /> All events
      </Link>

      {event.coverImageUrl && (
        <div className="mb-6 aspect-[16/6] overflow-hidden rounded-card border border-hairline bg-white/5 shadow-card">
          <img src={event.coverImageUrl} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      {/* Brand bar — client brand logo + name with the accent as a hairline. */}
      {(company || clientName) && (
        <div className="mb-4 flex items-center gap-2.5 border-l-2 pl-3" style={{ borderColor: accent }}>
          <BrandMark brand={company} />
          <span className="text-[13px] font-medium text-ink-soft">
            {company?.name ?? clientName}
            {agencyName && <span className="text-ink-faint"> · produced by {agencyName}</span>}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="max-w-2xl font-serif text-[36px] leading-tight tracking-tight">
          {event.name}
        </h1>
        <StatusPill status={event.status} />
      </div>

      <div className="mt-3 flex flex-col gap-1.5 text-[14px] text-ink-soft">
        <span className="flex items-center gap-2">
          <CalendarDays size={15} className="shrink-0 text-champagne" />
          {formatRange(event.startsAt, event.endsAt)}
        </span>
        {(event.venueName || event.addressLocality) && (
          <span className="flex items-center gap-2">
            <MapPin size={15} className="shrink-0 text-champagne" />
            {[event.venueName, event.addressLocality, event.addressRegion]
              .filter(Boolean)
              .join(" · ")}
          </span>
        )}
        {event.hostName && (
          <span className="flex items-center gap-2">
            <UserRound size={15} className="shrink-0 text-champagne" />
            Hosted by {event.hostName}
          </span>
        )}
      </div>

      <Tabs tab={tab} onChange={setTab} accent={accent} />

      <div className="mt-6">
        {tab === "overview" && <OverviewTab event={event} />}
        {tab === "report" && <ReportTab eventId={eventId} accent={accent} />}
        {tab === "guests" && <GuestsTab eventId={eventId} />}
      </div>
    </div>
  );
}

function Tabs({
  tab,
  onChange,
  accent,
}: {
  tab: Tab;
  onChange: (t: Tab) => void;
  accent: string;
}) {
  const items: Array<{ id: Tab; label: string }> = [
    { id: "overview", label: "Overview" },
    { id: "report", label: "Report" },
    { id: "guests", label: "Guests" },
  ];
  return (
    <div className="mt-7 flex gap-6 border-b border-hairline" role="tablist">
      {items.map((item) => {
        const active = item.id === tab;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={`-mb-px border-b-2 pb-2.5 text-[14px] font-medium transition-colors ${
              active ? "text-ink" : "border-transparent text-ink-soft hover:text-ink"
            }`}
            style={active ? { borderColor: accent } : undefined}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// ----- Overview --------------------------------------------------------------

function OverviewTab({ event }: { event: PortalEventDetail }) {
  const address = [
    event.streetAddress,
    [event.addressLocality, event.addressRegion, event.postalCode].filter(Boolean).join(", "),
    event.addressCountry,
  ]
    .filter(Boolean)
    .join("\n");

  const facts: Array<{ label: string; value: string | null }> = [
    { label: "Venue", value: event.venueName },
    { label: "Address", value: address || null },
    { label: "Dates", value: formatRange(event.startsAt, event.endsAt) },
    { label: "Timezone", value: event.ianaTimezone },
    { label: "Capacity", value: event.maxCapacity != null ? String(event.maxCapacity) : null },
    { label: "RSVP deadline", value: formatDate(event.rsvpDeadline) },
    { label: "Status", value: titleCase(event.status) },
    { label: "Host", value: event.hostName },
  ];

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
      <div>
        {event.description ? (
          <div className="max-w-2xl whitespace-pre-wrap text-[15px] leading-relaxed text-ink-soft">
            {event.description}
          </div>
        ) : (
          <p className="text-[14px] text-ink-soft">No description has been added yet.</p>
        )}
      </div>

      <aside className="luxe-panel h-fit rounded-card p-5">
        <div className="eyebrow mb-3">Details</div>
        <dl className="flex flex-col gap-3">
          {facts
            .filter((f) => f.value)
            .map((f) => (
              <div key={f.label}>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.10em] text-ink-faint">
                  {f.label}
                </dt>
                <dd className="mt-0.5 whitespace-pre-line text-[14px] text-ink-soft">{f.value}</dd>
              </div>
            ))}
        </dl>
      </aside>
    </div>
  );
}

// ----- Report ------------------------------------------------------------------

function ReportTab({ eventId, accent }: { eventId: string; accent: string }) {
  const { data, error, loading, reload } = useApi(() => getPortalReport(eventId), [eventId]);

  if (loading) return <LoadingState label="Building report…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const { report } = data;
  const statusEntries = Object.entries(report.guests.byStatus).sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile label="Guests invited" value={report.guests.total} />
        <StatTile label="Confirmed headcount" value={report.guests.confirmedHeadcount} accent={accent} />
        <StatTile label="Checked in" value={report.guests.checkedIn} />
      </div>

      {statusEntries.length > 0 && (
        <section>
          <div className="eyebrow mb-3">RSVPs by status</div>
          <div className="flex flex-wrap gap-2">
            {statusEntries.map(([status, count]) => (
              <span
                key={status}
                className="inline-flex items-center gap-2 rounded-full border border-hairline bg-card px-3 py-1.5 text-[13px] shadow-card"
              >
                <StatusPill status={status} />
                <span className="font-semibold tabular-nums">{count}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="eyebrow mb-3">Sessions</div>
        {report.agenda.length === 0 ? (
          <EmptyState title="No sessions yet" body="Per-session RSVPs will appear once the agenda is published." />
        ) : (
          <div className="overflow-x-auto rounded-card border border-hairline bg-card shadow-card">
            <table className="table-base min-w-[560px]">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>When</th>
                  <th className="text-right">Confirmed</th>
                  <th className="text-right">Waitlisted</th>
                  <th className="text-right">Checked in</th>
                </tr>
              </thead>
              <tbody>
                {report.agenda.map((item) => (
                  <tr key={item.agendaItemId}>
                    <td className="font-medium text-ink">{item.name}</td>
                    <td className="whitespace-nowrap text-ink-soft">
                      {formatSessionWindow(item.startsAt, item.endsAt)}
                    </td>
                    <td className="text-right tabular-nums">{item.confirmed}</td>
                    <td className="text-right tabular-nums">{item.waitlisted}</td>
                    <td className="text-right tabular-nums">{item.checkedIn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ----- Guests --------------------------------------------------------------------

function GuestsTab({ eventId }: { eventId: string }) {
  const { data, error, loading, reload } = useApi(() => getPortalGuests(eventId), [eventId]);
  const [query, setQuery] = useState("");

  const guests = data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter((g) =>
      [guestName(g), g.emailAtInvite, g.companyAtInvite, g.titleAtInvite]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q)),
    );
  }, [guests, query]);

  if (loading) return <LoadingState label="Loading guest list…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (guests.length === 0) {
    return <EmptyState title="No guests yet" body="The roster will appear once invitations go out." />;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="relative block w-full max-w-xs">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-champagne" />
          <input
            type="search"
            placeholder="Search name, email, company…"
            className="field py-2 pl-9 text-[14px]"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <span className="text-[12.5px] text-ink-faint">
          {filtered.length} of {guests.length} guests
        </span>
      </div>

      <div className="overflow-x-auto rounded-card border border-hairline bg-card shadow-card">
        <table className="table-base min-w-[960px]">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Email</th>
              <th>Title / Company</th>
              <th>Status</th>
              <th className="text-right">+1s</th>
              <th>Dietary</th>
              <th>Responded</th>
              <th>Checked in</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <GuestRow key={g.guestId} guest={g} />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-ink-soft">
                  No guests match “{query}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function guestName(g: PortalGuest): string {
  const full = [g.firstName, g.lastName].filter(Boolean).join(" ");
  return g.displayName || full || g.nameAtInvite || g.emailAtInvite || "Guest";
}

function GuestRow({ guest }: { guest: PortalGuest }) {
  const titleCompany = [guest.titleAtInvite, guest.companyAtInvite].filter(Boolean).join(" · ");
  const dietary = [guest.dietaryNotes, guest.specialRequests].filter(Boolean).join(" — ");
  const plusOnes =
    guest.guestCount != null && guest.guestCount > 0
      ? guest.maxGuests != null
        ? `${guest.guestCount} / ${guest.maxGuests}`
        : String(guest.guestCount)
      : "—";

  return (
    <tr>
      <td>
        <div className="font-medium text-ink">{guestName(guest)}</div>
        {guest.relationshipToHost && (
          <div className="text-[12px] text-ink-faint">{titleCase(guest.relationshipToHost)}</div>
        )}
      </td>
      <td className="whitespace-nowrap text-ink-soft">{guest.emailAtInvite ?? "—"}</td>
      <td className="text-ink-soft">{titleCompany || "—"}</td>
      <td>
        <StatusPill status={guest.status} />
        {guest.responseNote && (
          <div className="mt-1 max-w-[220px] text-[12px] text-ink-faint">{guest.responseNote}</div>
        )}
      </td>
      <td className="whitespace-nowrap text-right tabular-nums">{plusOnes}</td>
      <td className="max-w-[220px] text-ink-soft">{dietary || "—"}</td>
      <td className="whitespace-nowrap text-ink-soft">{formatStamp(guest.respondedAt) ?? "—"}</td>
      <td className="whitespace-nowrap text-ink-soft">{formatStamp(guest.checkedInAt) ?? "—"}</td>
    </tr>
  );
}
